import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { datadogRum } from '@datadog/browser-rum';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@savvy-kit/components/collapsible';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@savvy-kit/components/hover-card';
import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@savvy-kit/templates/sidebar';
import { isNil } from 'es-toolkit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { BookOpen, ChevronRight, Handshake, Landmark, MessageCircleQuestion, TrendingUp, Users } from 'lucide-react';
import { useOrganizationBrandingEnabled } from 'src/components/advisor-dashboard/branding/use-organization-branding-enabled';
import { useReportIssueModal } from 'src/components/advisor-dashboard/report-issue/use-report-issue-modal';
import { isScV3Enabled } from 'src/components/advisor-dashboard/service-calendar-v3/flags';
import { SERVICE_CALENDAR_V3_HREF } from 'src/components/advisor-dashboard/service-calendar-v3/routes';
import { TEMPLATES_HREF } from 'src/features/meeting-agenda-templates/templates-routes';
import { DatadogRumAction } from 'src/utils/logging';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import clientEnv from '@savvy/env/client';

import type { NavGroup, NavItem } from './admin-nav-group';
import { ADMIN_NAV_GROUP } from './admin-nav-group';
import AdvisorSideNavFooter from './advisor-side-nav-footer';
import AdvisorSideNavHeader from './advisor-side-nav-header';
import { filterNavGroups } from './filter-nav-groups';
import { buildReportingUrlFromCurrentPath } from './reporting-url';
import ShareFeedbackMenuButton from './share-feedback-menu-button';

function SideNav() {
  const { user } = useAuth();
  const currentPath = usePathname() ?? '';

  const { state } = useSidebar();
  const isSideNavExpanded = state === 'expanded';
  const allFlags = useFlags<Record<string, boolean>>();
  const { enabled: isOrganizationBrandingEnabled, loading: isOrganizationBrandingEnabledLoading } =
    useOrganizationBrandingEnabled();
  const openReportIssueModal = useReportIssueModal((s) => s.openReportIssueModal);

  const reportingUrl = buildReportingUrlFromCurrentPath(currentPath);

  const navGroups: NavGroup[] = [
    {
      title: 'Client Management',
      icon: Handshake,
      items: [
        {
          title: 'Clients',
          url: '/advisor/dashboard/clients',
          subUrls: ['/advisor/dashboard/client', '/advisor/dashboard/accounts', '/advisor/dashboard/savvy-tax'],
        },
        {
          title: 'Opportunities',
          url: '/advisor/dashboard/opportunities',
        },
        {
          title: 'Tasks',
          url: '/advisor/dashboard/tasks',
        },
        {
          title: 'Professional Contacts',
          url: '/advisor/dashboard/professional-contacts',
          featureFlag: (flags): boolean => Boolean(flags['flagProfessionalContacts']),
        },
        {
          title: 'Service Items',
          url: '/advisor/dashboard/service-items',
          featureFlag: (flags): boolean => Boolean(flags['serviceItems'] && !flags['flagServiceCalendarV2']),
        },
        {
          title: 'Service Calendar',
          url: '/advisor/dashboard/service-calendar',
          featureFlag: (flags): boolean => Boolean(flags['flagServiceCalendarV2']),
        },
        {
          title: 'Service Calendar (v3)',
          url: SERVICE_CALENDAR_V3_HREF,
          featureFlag: (flags): boolean => isScV3Enabled(flags),
        },
        {
          title: 'Reporting',
          url: reportingUrl,
          // The Reporting item is active whenever we're on /advisor/dashboard/reporting,
          // regardless of any pre-population query params we appended above.
          subUrls: ['/advisor/dashboard/reporting'],
          onClick: () => {
            datadogRum.addAction(DatadogRumAction.ReportingClicked);
          },
        },
        {
          title: 'RMD Tracker',
          url: '/advisor/dashboard/rmds',
          onClick: () => {
            datadogRum.addAction(DatadogRumAction.RMDTrackerClicked);
          },
        },
        {
          title: 'Tags',
          url: '/advisor/dashboard/tags',
        },
        {
          title: 'Resources',
          url: TEMPLATES_HREF,
          featureFlag: (flags): boolean => Boolean(flags['flagMeetingAgendaTemplates']),
        },
        {
          title: 'Retirement Plans',
          url: '/advisor/dashboard/retirement-plans',
          featureFlag: 'flagRetirementPlans',
        },
      ],
    },
    {
      title: 'Growth & Marketing',
      icon: TrendingUp,
      items: [
        {
          title: 'Marketing Center',
          url: '/advisor/dashboard/marketing',
        },
        {
          title: 'COI Rep Manager',
          url: '/advisor/dashboard/cois',
          featureFlag: (flags): boolean => !flags['flagProfessionalContacts'],
          onClick: () => {
            datadogRum.addAction(DatadogRumAction.CoiRepManagerClicked);
          },
        },
        {
          title: 'Organization Branding',
          url: '/advisor/dashboard/organization-branding',
          // Not a plain flag lookup: the org's own setting wins when it has one,
          // and only falls back to LaunchDarkly when it doesn't. Stay hidden until
          // the org setting resolves, or an org whose setting disagrees with the
          // LaunchDarkly fallback sees the item flip after load.
          featureFlag: () => !isOrganizationBrandingEnabledLoading && isOrganizationBrandingEnabled,
        },
      ],
    },
    {
      title: 'Investment Management',
      icon: BookOpen,
      items: [
        {
          title: 'Investment Resources',
          url: '/advisor/dashboard/offerings',
          onClick: () => {
            datadogRum.addAction(DatadogRumAction.InvestmentResourcesClicked);
          },
        },
        {
          title: 'Financial Planning',
          url: '/financial-planning',
          openInNewTab: true,
        },
        {
          title: 'Alternative Funds',
          url: '/advisor/dashboard/alternative-funds',
          onClick: () => {
            datadogRum.addAction(DatadogRumAction.SavvyAlternativeFundsClicked);
          },
        },
      ],
    },
    {
      title: 'Advisor Success',
      icon: Users,
      items: [
        {
          title: 'Directory',
          url: '/advisor/dashboard/directory',
          featureFlag: 'flagAdvisorDirectory',
        },
        {
          title: 'Documentation',
          url: '/advisor/dashboard/documentation',
          featureFlag: 'flagNotionProxy',
          onClick: () => {
            datadogRum.addAction(DatadogRumAction.DocumentationClicked);
          },
        },
      ],
    },
    {
      title: 'Operations',
      icon: Landmark,
      items: [
        {
          title: 'Linked Bank Accounts',
          url: '/advisor/dashboard/linked-bank-accounts',
        },
      ],
      featureFlag: 'flagLinkedBankAdvisorImport',
    },
  ];

  const servalSupport = Boolean(allFlags['flagServalSupport']);

  if (servalSupport) {
    navGroups.push({
      title: 'Support',
      icon: MessageCircleQuestion,
      items: [
        {
          title: 'Share Feedback',
          url: '/advisor/dashboard/share_feedback',
          onClick: () => {
            datadogRum.addAction(DatadogRumAction.ShareFeedbackClicked);
          },
        },
        {
          title: 'Report an Issue',
          url: '/advisor/dashboard/report_issue',
          onClick: () => {
            datadogRum.addAction(DatadogRumAction.ReportAnIssueClicked);
          },
          onActivate: () => {
            openReportIssueModal();
          },
        },
      ],
    });
  }

  if (
    clientEnv.NEXT_PUBLIC_VERCEL_ENV === 'development' ||
    user?.role === 'ADMIN' ||
    user?.role === 'ADMIN_CSA' ||
    user?.role === 'READ_ONLY_ADMIN'
  ) {
    navGroups.push(ADMIN_NAV_GROUP);
  }

  // Apply feature flag filtering
  const filteredNavGroups = filterNavGroups(navGroups, allFlags);

  const isNavItemActive = (navItem: NavItem) => {
    return (
      currentPath.includes(navItem.url) ||
      (!isNil(navItem.subUrls) && navItem.subUrls.some((subUrl) => currentPath.includes(subUrl)))
    );
  };

  const isNavGroupActive = (items: NavItem[]): boolean => {
    return items.some((item) => isNavItemActive(item));
  };

  const renderNavItem = (navItem: NavItem) => {
    if (navItem.onActivate) {
      const onActivate = navItem.onActivate;
      return (
        <SidebarMenuSubItem key={navItem.title}>
          <SidebarMenuSubButton
            render={
              <button
                type="button"
                onClick={() => {
                  navItem.onClick?.();
                  onActivate();
                }}
              />
            }
            isActive={isNavItemActive(navItem)}
          >
            <span>{navItem.title}</span>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      );
    }
    return (
      <SidebarMenuSubItem key={navItem.title}>
        <SidebarMenuSubButton
          render={
            <NextLink
              href={navItem.url}
              target={navItem.openInNewTab === true ? '_blank' : '_self'}
              onClick={() => {
                navItem.onClick?.();
              }}
            />
          }
          isActive={isNavItemActive(navItem)}
        >
          <span>{navItem.title}</span>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  };

  return (
    <Sidebar className="SavvyAdvisorMainSidenav" collapsible="icon">
      <AdvisorSideNavHeader />
      <SidebarContent>
        <SidebarMenu>
          {filteredNavGroups.map((navGroup) => (
            <Collapsible
              key={navGroup.title}
              render={<SidebarMenuItem className="group/collapsible" />}
              defaultOpen={isNavGroupActive(navGroup.items)}
            >
              <HoverCard key={String(isSideNavExpanded)} open={isSideNavExpanded ? false : undefined}>
                <CollapsibleTrigger
                  render={
                    <HoverCardTrigger render={<SidebarMenuButton className="whitespace-nowrap w-full" />}>
                      <navGroup.icon />
                      <span>{navGroup.title}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[open]/collapsible:rotate-90" />
                    </HoverCardTrigger>
                  }
                />

                <HoverCardContent
                  side="right"
                  align="start"
                  sideOffset={24}
                  className="p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <SidebarMenuSub className="border-none mx-0 p-0">
                    {navGroup.items.map((navItem) => renderNavItem(navItem))}
                  </SidebarMenuSub>
                </HoverCardContent>
              </HoverCard>

              <CollapsibleContent className="h-0 data-[open]:h-[var(--collapsible-panel-height)] overflow-hidden transition-[height] duration-200 ease-in-out">
                <SidebarMenuSub>{navGroup.items.map((navItem) => renderNavItem(navItem))}</SidebarMenuSub>
              </CollapsibleContent>
            </Collapsible>
          ))}
          {!servalSupport && (
            <SidebarMenuItem>
              <ShareFeedbackMenuButton />
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarContent>
      <AdvisorSideNavFooter />
    </Sidebar>
  );
}

export default SideNav;
